import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Car, Sparkles } from 'lucide-react';
import { formatPrice } from '@/lib/formatters';
import { powertrainLabels, bodyTypeLabels, getPowertrainBadgeVariant } from '@/types/database';
import { useModelsCatalog } from '@/hooks/useModelsCatalog';
import type { Tables } from '@/integrations/supabase/types';

type Variant = Tables<'variants'>;
type Model = Tables<'models'>;
type Brand = Tables<'brands'>;
type PowertrainSpecs = Tables<'powertrain_specs'>;

interface SimilarCarData {
  variant: Variant;
  model: Model;
  brand: Brand;
  specs: PowertrainSpecs | null;
  lowestPrice: number | null;
  similarityScore: number;
  matchReasons: string[];
}

interface SimilarCarsProps {
  currentVariant: Variant;
  currentModel: Model;
  currentSpecs: PowertrainSpecs | null;
  currentPrice: number | null;
}

// Similarity scoring weights
const WEIGHTS = {
  powertrain: 25,     // Same powertrain type is important
  bodyType: 20,       // Same body type
  priceRange: 20,     // Within 20% price range
  batteryRange: 15,   // Similar battery/range for EVs
  size: 10,           // Similar trunk/weight
  power: 10,          // Similar power output
};

/** Maks. antal kandidater vi henter specs/priser for — holder bilsiden let. */
const CANDIDATE_LIMIT = 60;

function calculateSimilarity(
  current: {
    variant: Variant;
    model: Model;
    specs: PowertrainSpecs | null;
    price: number | null;
  },
  candidate: {
    variant: Variant;
    model: Model;
    specs: PowertrainSpecs | null;
    price: number | null;
  }
): { score: number; reasons: string[] } {
  let score = 0;
  const reasons: string[] = [];
  const candidatePrice = candidate.price;

  // Same powertrain type
  if (current.variant.powertrain_type === candidate.variant.powertrain_type) {
    score += WEIGHTS.powertrain;
    reasons.push(powertrainLabels[current.variant.powertrain_type]);
  }

  // Same body type
  if (current.model.body_type === candidate.model.body_type) {
    score += WEIGHTS.bodyType;
    reasons.push(bodyTypeLabels[current.model.body_type]);
  }

  // Price within 20%
  if (current.price && candidatePrice) {
    const priceDiff = Math.abs(current.price - candidatePrice) / current.price;
    if (priceDiff <= 0.2) {
      score += WEIGHTS.priceRange * (1 - priceDiff / 0.2);
      reasons.push('Lignende pris');
    }
  }

  // Range similarity
  if (current.specs?.range_wltp_km && candidate.specs?.range_wltp_km) {
    const rangeDiff =
      Math.abs(current.specs.range_wltp_km - candidate.specs.range_wltp_km) / current.specs.range_wltp_km;
    if (rangeDiff <= 0.25) {
      score += WEIGHTS.batteryRange * (1 - rangeDiff / 0.25);
      if (!reasons.includes('Lignende rækkevidde')) reasons.push('Lignende rækkevidde');
    }
  }

  // Battery capacity similarity
  if (current.specs?.battery_kwh && candidate.specs?.battery_kwh) {
    const batteryDiff = Math.abs(current.specs.battery_kwh - candidate.specs.battery_kwh) / current.specs.battery_kwh;
    if (batteryDiff <= 0.2) {
      score += WEIGHTS.batteryRange * 0.5 * (1 - batteryDiff / 0.2);
    }
  }

  // Size similarity (trunk)
  if (current.variant.trunk_l && candidate.variant.trunk_l) {
    const trunkDiff = Math.abs(current.variant.trunk_l - candidate.variant.trunk_l) / current.variant.trunk_l;
    if (trunkDiff <= 0.25) {
      score += WEIGHTS.size * (1 - trunkDiff / 0.25);
      if (!reasons.includes('Lignende størrelse')) reasons.push('Lignende størrelse');
    }
  }

  // Power similarity
  if (current.specs?.power_hp && candidate.specs?.power_hp) {
    const powerDiff = Math.abs(current.specs.power_hp - candidate.specs.power_hp) / current.specs.power_hp;
    if (powerDiff <= 0.25) {
      score += WEIGHTS.power * (1 - powerDiff / 0.25);
      if (!reasons.includes('Lignende ydelse')) reasons.push('Lignende ydelse');
    }
  }

  return { score, reasons };
}

export function SimilarCars({ currentVariant, currentModel, currentSpecs, currentPrice }: SimilarCarsProps) {
  const catalog = useModelsCatalog();

  const { data: similarCars, isLoading } = useQuery({
    queryKey: ['similar-cars', currentVariant.id, catalog.data ? 'ready' : 'pending'],
    enabled: !!currentVariant.id && !!catalog.data,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<SimilarCarData[]> => {
      const cat = catalog.data!;

      // 1) Indsnævr kandidaterne serverside i stedet for at hente hele kataloget:
      //    samme karrosseritype er langt den stærkeste indikator, ellers samme mærke.
      const sameBodyModelIds = cat.models
        .filter((m) => m.body_type === currentModel.body_type)
        .map((m) => m.id);
      const sameBrandModelIds = cat.models
        .filter((m) => m.brand_id === currentModel.brand_id)
        .map((m) => m.id);
      const modelIds = Array.from(new Set([...sameBodyModelIds, ...sameBrandModelIds]));
      if (modelIds.length === 0) return [];

      let q = supabase
        .from('variants')
        .select('*')
        .eq('is_active', true)
        .eq('powertrain_type', currentVariant.powertrain_type)
        .neq('availability_status', 'discontinued')
        .neq('id', currentVariant.id)
        .in('model_id', modelIds)
        .limit(CANDIDATE_LIMIT * 3);

      const { data: candidates, error } = await q;
      if (error) throw error;
      if (!candidates || candidates.length === 0) return [];

      // 2) Forhåndssortér på pris-nærhed så vi kun henter specs/priser for de mest relevante.
      const prioritized = (candidates as Variant[])
        .slice()
        .sort((a, b) => {
          const sameBodyA = sameBodyModelIds.includes(a.model_id) ? 0 : 1;
          const sameBodyB = sameBodyModelIds.includes(b.model_id) ? 0 : 1;
          if (sameBodyA !== sameBodyB) return sameBodyA - sameBodyB;
          if (!currentPrice) return 0;
          const pa = a.expected_price_dkk ?? currentPrice;
          const pb = b.expected_price_dkk ?? currentPrice;
          return Math.abs(pa - currentPrice) - Math.abs(pb - currentPrice);
        })
        .slice(0, CANDIDATE_LIMIT);

      const ids = prioritized.map((v) => v.id);

      const [specsRes, pricesRes] = await Promise.all([
        supabase.from('powertrain_specs').select('*').in('variant_id', ids),
        supabase.from('prices_dk').select('variant_id, price_dkk').in('variant_id', ids),
      ]);

      const specsByVariant = new Map<string, PowertrainSpecs>();
      (specsRes.data || []).forEach((s) => specsByVariant.set(s.variant_id, s as PowertrainSpecs));

      const priceByVariant = new Map<string, number>();
      (pricesRes.data || []).forEach((p) => {
        const prev = priceByVariant.get(p.variant_id);
        if (prev === undefined || p.price_dkk < prev) priceByVariant.set(p.variant_id, p.price_dkk);
      });

      const scored = prioritized
        .map((v) => {
          const model = cat.byId.get(v.model_id);
          if (!model) return null;
          const specs = specsByVariant.get(v.id) || null;
          const lowestPrice = priceByVariant.get(v.id) ?? v.expected_price_dkk ?? null;

          const { score, reasons } = calculateSimilarity(
            { variant: currentVariant, model: currentModel, specs: currentSpecs, price: currentPrice },
            { variant: v, model, specs, price: lowestPrice }
          );

          return {
            variant: v,
            model,
            brand: model.brand,
            specs,
            lowestPrice,
            similarityScore: score,
            matchReasons: reasons,
          } as SimilarCarData;
        })
        .filter((x): x is SimilarCarData => x !== null);

      return scored
        .filter((car) => car.similarityScore >= 20)
        .sort((a, b) => b.similarityScore - a.similarityScore)
        .slice(0, 4);
    },
  });

  if (isLoading || !similarCars || similarCars.length === 0) {
    return null;
  }

  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          Lignende elbiler
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {similarCars.map((car) => (
            <Link
              key={car.variant.id}
              to={`/biler/${car.brand.slug}/${car.model.slug}/${car.variant.slug}`}
              className="group block rounded-lg border border-border hover:border-primary/50 hover:shadow-md transition-all overflow-hidden"
            >
              {/* Image */}
              <div className="aspect-[16/10] bg-muted relative overflow-hidden">
                {car.variant.image_url || car.model.image_url ? (
                  <img
                    src={car.variant.image_url || car.model.image_url!}
                    alt={`${car.brand.name} ${car.model.name}`}
                    width={480}
                    height={300}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Car className="w-10 h-10 text-muted-foreground/60" />
                  </div>
                )}
                <Badge
                  variant={getPowertrainBadgeVariant(car.variant.powertrain_type)}
                  className="absolute top-2 left-2 text-xs"
                >
                  {powertrainLabels[car.variant.powertrain_type]}
                </Badge>
              </div>

              {/* Info */}
              <div className="p-3">
                <p className="text-xs text-muted-foreground">{car.brand.name}</p>
                <p className="font-semibold text-sm group-hover:text-primary transition-colors line-clamp-1">
                  {car.model.name} {car.variant.name}
                </p>

                {/* Match reasons */}
                <div className="flex flex-wrap gap-1 mt-2">
                  {car.matchReasons.slice(0, 2).map((reason, idx) => (
                    <span
                      key={idx}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-accent/10 text-muted-foreground"
                    >
                      {reason}
                    </span>
                  ))}
                </div>

                {/* Price */}
                {car.lowestPrice && (
                  <p className="font-display font-bold text-primary mt-2 text-sm">
                    {formatPrice(car.lowestPrice)}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
